import { useEffect, useMemo, useState } from "react";
import {
  Card,
  EmptyState,
  Form,
  FormField,
  PageHeader,
  SelectInput,
  TextInput,
  useCreateEntity,
  useEntityDetail,
  useUpdateEntity,
} from "@pericles/framework-frontend";

import { enrichCustomerContext, type CustomerContextSource } from "../api/customerContextClient";
import { getCountryOptions } from "../utils/countries";

const sectorOptions = [
  { value: "tech", label: "Tech" },
  { value: "industry", label: "Industry" },
  { value: "services", label: "Services" },
  { value: "other", label: "Other" },
];

export interface CustomerFormValues {
  id?: string;
  name?: string;
  code?: string;
  sector?: string;
  countryCode?: string;
  context?: string;
  contextSources?: CustomerContextSource[];
  contextGeneratedAt?: string;
  contextGeneratedBy?: string;
  contextGenerationStatus?: string;
  contextGenerationError?: string | null;
}

interface CustomerFormPanelProps {
  mode: "create" | "edit";
  customerId?: string | number | null;
  onSaved?: () => void;
  onBack?: () => void;
}

const textareaClasses = [
  "block w-full rounded-md border border-slate-300 px-3 py-2 text-sm",
  "shadow-sm focus:border-slate-500 focus:ring-1 focus:ring-slate-500",
  "disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400",
].join(" ");

export function CustomerFormPanel({ mode, customerId, onSaved, onBack }: CustomerFormPanelProps): JSX.Element {
  const { data, isLoading, isError, error, refetch } = useEntityDetail<CustomerFormValues>({
    entity: "Customer",
    id: (customerId ?? "") as string,
    enabled: mode === "edit",
  });
  const createMutation = useCreateEntity<CustomerFormValues, any>({ entity: "Customer" });
  const updateMutation = useUpdateEntity<CustomerFormValues, any>({
    entity: "Customer",
    id: (customerId ?? "") as string,
  });

  const [formValues, setFormValues] = useState<CustomerFormValues>({
    name: "",
    code: "",
    sector: sectorOptions[0].value,
    countryCode: undefined,
    context: "",
    contextSources: [],
    contextGenerationStatus: "idle",
    contextGenerationError: null,
  });
  const [generationNotes, setGenerationNotes] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationMessage, setGenerationMessage] = useState<string | null>(null);

  useEffect(() => {
    if (mode === "edit" && data) {
      setFormValues({
        name: data.name,
        code: data.code,
        sector: data.sector,
        countryCode: data.countryCode,
        context: data.context,
        contextSources: (data.contextSources as CustomerContextSource[] | undefined) ?? [],
        contextGeneratedAt: data.contextGeneratedAt,
        contextGeneratedBy: data.contextGeneratedBy,
        contextGenerationStatus: data.contextGenerationStatus ?? "idle",
        contextGenerationError: data.contextGenerationError ?? null,
      });
    }
  }, [data, mode]);

  const countryOptions = useMemo(() => getCountryOptions(), []);
  const isSubmitting = mode === "create" ? createMutation.isSubmitting : updateMutation.isSubmitting;
  const submitError = mode === "create" ? createMutation.error : updateMutation.error;

  const handleFieldChange = (field: keyof CustomerFormValues, value: unknown) => {
    setFormValues((previous) => ({ ...previous, [field]: value }));
  };

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setGenerationMessage(null);

    if (mode === "create") {
      const result = await createMutation.create(formValues);
      if (result) {
        onSaved?.();
        setFormValues({
          name: "",
          code: "",
          sector: sectorOptions[0].value,
          countryCode: undefined,
          context: "",
          contextSources: [],
          contextGenerationStatus: "idle",
          contextGenerationError: null,
        });
      }
      return;
    }

    const result = await updateMutation.update(formValues);
    if (result) {
      onSaved?.();
      void refetch();
    }
  };

  const handleGenerateContext = async () => {
    if (!customerId) return;
    setIsGenerating(true);
    setGenerationMessage(null);
    setFormValues((previous) => ({ ...previous, contextGenerationStatus: "running", contextGenerationError: null }));

    try {
      const response = await enrichCustomerContext(String(customerId), {
        notes: generationNotes || undefined,
        force: true,
      });

      setFormValues((previous) => ({
        ...previous,
        context: response.context ?? previous.context,
        contextSources: response.sources ?? [],
        contextGeneratedAt: response.generatedAt ?? new Date().toISOString(),
        contextGeneratedBy: response.model,
        contextGenerationStatus: "done",
        contextGenerationError: null,
      }));
      setGenerationMessage(
        response.warnings?.length
          ? response.warnings.join("\n")
          : "Context generated. You can review and save any edits."
      );
    } catch (genError: any) {
      const message = genError instanceof Error ? genError.message : "Failed to generate context";
      setFormValues((previous) => ({ ...previous, contextGenerationStatus: "failed", contextGenerationError: message }));
      setGenerationMessage(message);
    } finally {
      setIsGenerating(false);
    }
  };

  const headerTitle = mode === "create" ? "Create customer" : "Edit customer";
  const generationStatus = (formValues.contextGenerationStatus ?? "idle").toString();
  const lastGenerated = formValues.contextGeneratedAt
    ? new Date(formValues.contextGeneratedAt).toLocaleString()
    : "Not generated";
  const sources = formValues.contextSources ?? [];

  if (mode === "edit" && !customerId) {
    return (
      <Card>
        <span className="text-sm text-red-600">Select a customer to edit.</span>
      </Card>
    );
  }

  if (mode === "edit" && isLoading) {
    return (
      <Card>
        <span className="text-sm text-slate-600">Loading…</span>
      </Card>
    );
  }

  if (mode === "edit" && isError) {
    return (
      <Card>
        <span className="text-sm text-red-600">{error instanceof Error ? error.message : "Failed to load customer"}</span>
      </Card>
    );
  }

  if (mode === "edit" && !data) {
    return <EmptyState title="Customer not found" description="The requested customer could not be found." />;
  }

  return (
    <div className="flex flex-col gap-3">
      <PageHeader title={headerTitle} onBack={mode === "edit" ? onBack : undefined} />
      <Form onSubmit={handleSave} className="flex flex-col gap-4">
        <Card className="flex flex-col gap-4">
          <FormField label="Name" required>
            <TextInput
              name="name"
              value={formValues.name ?? ""}
              onChange={(event) => handleFieldChange("name", event.target.value)}
              disabled={isSubmitting}
            />
          </FormField>
          <FormField label="Code" required>
            <TextInput
              name="code"
              value={formValues.code ?? ""}
              onChange={(event) => handleFieldChange("code", event.target.value)}
              disabled={isSubmitting}
            />
          </FormField>
          <FormField label="Sector" required>
            <SelectInput
              name="sector"
              value={formValues.sector ?? sectorOptions[0].value}
              onChange={(event) => handleFieldChange("sector", event.target.value || undefined)}
              disabled={isSubmitting}
            >
              {sectorOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectInput>
          </FormField>
          <FormField label="Country">
            <SelectInput
              name="countryCode"
              value={formValues.countryCode ?? ""}
              onChange={(event) => handleFieldChange("countryCode", event.target.value || undefined)}
              disabled={isSubmitting}
            >
              <option value="">--</option>
              {countryOptions.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name} ({country.code})
                </option>
              ))}
            </SelectInput>
          </FormField>
        </Card>

        <Card className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-col">
              <span className="text-base font-semibold text-slate-900">Context</span>
              <span className="text-sm text-slate-600">
                Status: <span className="font-semibold capitalize">{generationStatus}</span> • Last generated: {lastGenerated}
              </span>
              <span className="text-sm text-slate-600">Sources: {sources.length}</span>
            </div>
            {mode === "edit" ? (
              <div className="flex flex-col items-end gap-2">
                <label className="text-sm text-slate-600">
                  Notes for AI
                  <textarea
                    className={`${textareaClasses} mt-1 w-64`}
                    rows={2}
                    value={generationNotes}
                    onChange={(event) => setGenerationNotes(event.target.value)}
                    placeholder="Optional hints for the model"
                    disabled={isGenerating}
                  />
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="button button-secondary"
                    onClick={handleGenerateContext}
                    disabled={isGenerating}
                  >
                    {isGenerating ? "Generating…" : "Generate context with AI"}
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <textarea
            className={textareaClasses}
            rows={10}
            value={formValues.context ?? ""}
            onChange={(event) => handleFieldChange("context", event.target.value)}
            placeholder="Add customer context or let the AI generate it"
            disabled={isSubmitting}
          />

          {sources.length ? (
            <div className="flex flex-col gap-1">
              <span className="text-sm font-semibold text-slate-700">Sources</span>
              <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
                {sources.map((source, index) => (
                  <li key={`${source.url}-${index}`}>
                    <a className="text-blue-700 underline" href={source.url} target="_blank" rel="noreferrer">
                      {source.title || source.url}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {formValues.contextGenerationError ? (
            <span className="text-sm text-red-600">{formValues.contextGenerationError}</span>
          ) : null}
          {generationMessage ? <span className="text-sm text-slate-700 whitespace-pre-wrap">{generationMessage}</span> : null}
        </Card>

        <div className="flex items-center gap-3">
          <button type="submit" className="button button-primary" disabled={isSubmitting}>
            {isSubmitting ? "Saving…" : "Apply & Save"}
          </button>
          {submitError ? (
            <span className="text-sm text-red-600">
              {submitError instanceof Error ? submitError.message : "Failed to save"}
            </span>
          ) : null}
        </div>
      </Form>
    </div>
  );
}